import React, { useContext } from 'react'

import { useFlag } from '@unleash/proxy-client-react'
import { Routes, Route } from 'react-router-dom'

import CanRoute from '~/components/Authorisation/CanRoute'
import ErrorPage from '~/components/ErrorPage'
import { isHome, isPlayground } from '~/config'
import { ServiceContext, useSelectedServiceFkey, useSelectedServiceId } from '~/contexts/Service'

import * as perms from '~/modules/permissions'

import ChildGroups from '../ChildGroups'
import Children from '../Children'
import CommsCenter from '../CommsCenter'
import Dashboard from '../Dashboard'
import Educators from '../Educators'
import LearningAnalysis from '../LearningAnalysis'
import LearningFrameworks from '../LearningFrameworks'
import LiveLearning from '../LiveLearning'
import Moments from '../Moments'
import NutritionMenus from '../NutritionMenus'
import Observations from '../Observations'
import ParentFinance from '../ParentFinance'
import Planning from '../Planning'
import Reports from '../Reports'
import Rooms from '../Rooms'
import Search from '../Search'
import SearchWebView from '../Search/SearchWebView'
import Stories from '../Stories'
import Tables from '../Tables'

import TaggedDocumentsWebview from './components/TaggedDocumentsWebview'

const hasCohortTrackingFeature = (serviceFkey: string, services: Playground.SimpleService[]): boolean => {
  const selectedService = services.find((x) => x.fkey === serviceFkey)
  if (!selectedService) return false

  return perms.cohortTrackingEnabled(selectedService.features)
}

const hasTransportListFeature = (serviceFkey: string, services: Playground.SimpleService[]): boolean => {
  const selectedService = services.find((x) => x.fkey === serviceFkey)
  if (!selectedService) return false

  return perms.transportListsEnabled(selectedService.features)
}

interface Props {
  hasReports: 'true' | 'false'
  hasFrameworks: boolean
}

const RootRouter = ({ hasFrameworks, hasReports }: Props) => {
  const {
    state: { providerFkey, services },
  } = useContext(ServiceContext)
  const serviceFkey = useSelectedServiceFkey()
  const serviceId = useSelectedServiceId()
  const hasNutritionMenu = useFlag('edu-pes-nutrition-menu')

  if (!serviceFkey || (!providerFkey && isPlayground))
    return <ErrorPage heading="Error" message="Something went wrong" />

  return (
    <Routes>
      <Route path="/" element={<CanRoute perform={perms.dashboard.read} yes={() => <Dashboard />} />} />
      {isHome && (
        <Route
          path="messages/*"
          element={
            <CanRoute
              perform={perms.commsCenter.read}
              requiredHomeFeature="comms"
              yes={() => <CommsCenter serviceId={serviceId!} />}
            />
          }
        />
      )}
      {hasNutritionMenu && (
        <Route
          path="nutrition-menus/*"
          element={<CanRoute perform={perms.nutritionMenu.read} yes={() => <NutritionMenus />} />}
        />
      )}
      <Route path="moments/*" element={<CanRoute perform={perms.moments.read} yes={() => <Moments />} />} />
      <Route
        path="observations/*"
        element={<CanRoute perform={perms.observations.read} yes={() => <Observations />} />}
      />
      <Route
        path="stories/*"
        element={
          <CanRoute
            perform={perms.learningStories.read}
            requiredHomeFeature="learning_events"
            yes={() => <Stories providerFkey={providerFkey!} serviceFkey={serviceFkey} />}
          />
        }
      />
      <Route
        path="planning/*"
        element={
          <CanRoute
            perform={perms.learningPlans.read}
            requiredHomeFeature="learning_events"
            yes={() => <Planning providerFkey={providerFkey!} serviceFkey={serviceFkey} />}
          />
        }
      />
      <Route
        path="tables/*"
        element={
          <CanRoute
            perform={perms.learningTables.read}
            requiredHomeFeature="learning_events"
            yes={() => <Tables providerFkey={providerFkey} serviceFkey={serviceFkey} />}
          />
        }
      />
      {isPlayground && (
        <Route
          path="educators/*"
          element={
            <CanRoute
              perform={perms.educatorProfiles.read}
              yes={() => <Educators serviceFkey={serviceFkey} />}
            />
          }
        />
      )}
      <Route
        path="children/*"
        element={
          <CanRoute
            perform={perms.childrenProfiles.read}
            yes={() => <Children hasFrameworks={hasFrameworks} />}
          />
        }
      />
      {isPlayground && (
        <Route
          path="rooms/*"
          element={<CanRoute perform={perms.roomProfiles.read} yes={() => <Rooms />} />}
        />
      )}
      <Route
        path="search"
        element={<CanRoute perform={'search:read'} yes={() => <Search serviceFkey={serviceFkey} />} />}
      />
      <Route
        path="search/webview"
        element={<CanRoute perform={'search:read'} yes={() => <SearchWebView serviceFkey={serviceFkey} />} />}
      />
      <Route
        path="tagged-documents/webview"
        element={
          <CanRoute
            perform={'search:read'}
            yes={() => <TaggedDocumentsWebview serviceFkey={serviceFkey} />}
          />
        }
      />
      {isHome && (
        <Route
          path="parent-finance/*"
          element={
            <CanRoute
              perform={perms.parentFinance.read}
              requiredHomeFeature="finance"
              yes={() => <ParentFinance />}
            />
          }
        />
      )}
      {isPlayground && (
        <Route
          path="reports/*"
          element={
            <CanRoute
              perform={hasReports}
              permissions={['true']}
              yes={() => (
                <Reports
                  serviceFkey={serviceFkey}
                  hasCohortTracking={hasCohortTrackingFeature(serviceFkey, services)}
                  hasTransportLists={hasTransportListFeature(serviceFkey, services)}
                />
              )}
            />
          }
        />
      )}
      <Route
        path="live-learning/*"
        element={
          <CanRoute perform={perms.liveLearning.read} yes={() => <LiveLearning serviceId={serviceId!} />} />
        }
      />
      <Route
        path="child-groups/*"
        element={
          <CanRoute perform={perms.childGroups.read} yes={() => <ChildGroups serviceFkey={serviceFkey} />} />
        }
      />
      {hasFrameworks ? (
        <Route
          path="learning-analysis/*"
          element={
            <CanRoute
              perform={perms.learningAnalysis.read}
              yes={() => <LearningAnalysis serviceFkey={serviceFkey} />}
            />
          }
        />
      ) : null}
      {isPlayground && providerFkey && (
        <Route
          path="learning-frameworks/*"
          element={
            <CanRoute
              perform={perms.learningFrameworks.writeProvider}
              yes={() => <LearningFrameworks providerFkey={providerFkey} />}
            />
          }
        />
      )}
    </Routes>
  )
}

RootRouter.displayName = 'RootRouter'

export default RootRouter
